import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
    Plus, Wrench, KeyRound, Copy, ExternalLink, Edit, Trash2, Eye, EyeOff, 
    Search, Lock, Building2, Shield
} from 'lucide-react';

const AccessPage = () => {
    const { api, user } = useAuth();
    const [activeTab, setActiveTab] = useState('tools');
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Tools state
    const [tools, setTools] = useState([]);
    const [showToolModal, setShowToolModal] = useState(false);
    const [editingTool, setEditingTool] = useState(null);
    const [toolForm, setToolForm] = useState({
        name: '', username: '', email: '', password: '', website_url: '', description: ''
    });
    
    // Passwords state
    const [passwords, setPasswords] = useState([]);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordForm, setPasswordForm] = useState({
        name: '', username: '', email: '', password: '', website_url: '', notes: ''
    });
    
    // Visibility state
    const [showPasswords, setShowPasswords] = useState({});

    const canEditTools = user?.role === 'chef' || user?.role === 'buchhaltung';

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [toolsRes, pwdsRes] = await Promise.all([
                api.get('/tools'),
                api.get('/passwords')
            ]);
            setTools(toolsRes.data);
            setPasswords(pwdsRes.data);
        } catch (error) {
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    };

    // Tool handlers
    const handleSaveTool = async () => {
        try {
            if (editingTool) {
                await api.put(`/tools/${editingTool.id}`, toolForm);
                toast.success('Tool aktualisiert');
            } else {
                await api.post('/tools', toolForm);
                toast.success('Tool erstellt');
            }
            setShowToolModal(false);
            setEditingTool(null);
            setToolForm({ name: '', username: '', email: '', password: '', website_url: '', description: '' });
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Speichern');
        }
    };

    const handleDeleteTool = async (toolId) => {
        if (!window.confirm('Tool wirklich löschen?')) return;
        try {
            await api.delete(`/tools/${toolId}`);
            toast.success('Tool gelöscht');
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const openEditTool = (tool) => {
        setEditingTool(tool);
        setToolForm({
            name: tool.name,
            username: tool.username,
            email: tool.email,
            password: tool.password === '••••••••' ? '' : tool.password,
            website_url: tool.website_url,
            description: tool.description
        });
        setShowToolModal(true);
    };

    // Password handlers
    const handleSavePassword = async () => {
        try {
            await api.post('/passwords', passwordForm);
            toast.success('Passwort gespeichert');
            setShowPasswordModal(false);
            setPasswordForm({ name: '', username: '', email: '', password: '', website_url: '', notes: '' });
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Speichern');
        }
    };

    const handleDeletePassword = async (passwordId) => {
        if (!window.confirm('Passwort wirklich löschen?')) return;
        try {
            await api.delete(`/passwords/${passwordId}`);
            toast.success('Passwort gelöscht');
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        toast.success(`${field} kopiert`);
    };

    const togglePassword = (id) => {
        setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Filtering
    const filteredTools = tools.filter(t => 
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredPasswords = passwords.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8" data-testid="access-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        🔐 Zugänge
                    </h1>
                    <p className="text-muted-foreground">Tools & persönliche Passwörter</p>
                </div>
                <div className="flex items-center gap-2">
                    {activeTab === 'tools' && canEditTools && (
                        <Button onClick={() => { setEditingTool(null); setToolForm({ name: '', username: '', email: '', password: '', website_url: '', description: '' }); setShowToolModal(true); }} data-testid="add-tool-button">
                            <Plus className="w-4 h-4 mr-2" />
                            Tool hinzufügen
                        </Button>
                    )}
                    {activeTab === 'passwords' && (
                        <Button onClick={() => { setPasswordForm({ name: '', username: '', email: '', password: '', website_url: '', notes: '' }); setShowPasswordModal(true); }} data-testid="add-password-button">
                            <Plus className="w-4 h-4 mr-2" />
                            Passwort hinzufügen
                        </Button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <TabsList className="grid w-full md:w-auto grid-cols-2">
                        <TabsTrigger value="tools" className="gap-2">
                            <Wrench className="w-4 h-4" />
                            <span className="hidden sm:inline">Unternehmens-</span>Tools
                        </TabsTrigger>
                        <TabsTrigger value="passwords" className="gap-2">
                            <KeyRound className="w-4 h-4" />
                            Meine Passwörter
                        </TabsTrigger>
                    </TabsList>
                    
                    {/* Search */}
                    <div className="relative w-full md:w-80">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder={activeTab === 'tools' ? 'Tools suchen...' : 'Passwörter suchen...'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                            data-testid="access-search"
                        />
                    </div>
                </div>

                {/* Tools Tab */}
                <TabsContent value="tools">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredTools.map(tool => (
                            <motion.div
                                key={tool.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <Card className="shadow-soft h-full" data-testid={`tool-card-${tool.id}`}>
                                    <CardHeader className="pb-2">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                                    <Wrench className="w-5 h-5 text-primary" />
                                                </div>
                                                <div>
                                                    <CardTitle className="text-lg">{tool.name}</CardTitle>
                                                    {tool.description && (
                                                        <p className="text-xs text-muted-foreground">{tool.description}</p>
                                                    )}
                                                </div>
                                            </div>
                                            {canEditTools && (
                                                <div className="flex gap-1">
                                                    <Button variant="ghost" size="icon" onClick={() => openEditTool(tool)}>
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => handleDeleteTool(tool.id)}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        {tool.username && (
                                            <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                                                <div>
                                                    <p className="text-xs text-muted-foreground">Benutzername</p>
                                                    <p className="text-sm font-mono">{tool.username}</p>
                                                </div>
                                                <Button variant="ghost" size="icon" onClick={() => copyToClipboard(tool.username, 'Benutzername')}>
                                                    <Copy className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        )}
                                        {tool.email && (
                                            <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                                                <div>
                                                    <p className="text-xs text-muted-foreground">E-Mail</p>
                                                    <p className="text-sm font-mono">{tool.email}</p>
                                                </div>
                                                <Button variant="ghost" size="icon" onClick={() => copyToClipboard(tool.email, 'E-Mail')}>
                                                    <Copy className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                                            <div>
                                                <p className="text-xs text-muted-foreground">Passwort</p>
                                                <p className="text-sm font-mono">
                                                    {showPasswords[`tool-${tool.id}`] ? tool.password : '••••••••'}
                                                </p>
                                            </div>
                                            <div className="flex gap-1">
                                                <Button variant="ghost" size="icon" onClick={() => togglePassword(`tool-${tool.id}`)}>
                                                    {showPasswords[`tool-${tool.id}`] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => copyToClipboard(tool.password, 'Passwort')}>
                                                    <Copy className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                        {tool.website_url && (
                                            <Button 
                                                variant="outline" 
                                                className="w-full" 
                                                onClick={() => window.open(tool.website_url.startsWith('http') ? tool.website_url : `https://${tool.website_url}`, '_blank')}
                                            >
                                                <ExternalLink className="w-4 h-4 mr-2" />
                                                Website öffnen
                                            </Button>
                                        )}
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                        {filteredTools.length === 0 && (
                            <div className="col-span-full text-center py-12 text-muted-foreground">
                                <Wrench className="w-12 h-12 mx-auto mb-4 opacity-20" />
                                <p>Keine Tools gefunden</p>
                            </div>
                        )}
                    </div>
                </TabsContent>

                {/* Passwords Tab */}
                <TabsContent value="passwords">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredPasswords.map(pwd => (
                            <motion.div
                                key={pwd.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <Card className="shadow-soft h-full" data-testid={`password-card-${pwd.id}`}>
                                    <CardHeader className="pb-2">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                                    <KeyRound className="w-5 h-5 text-primary" />
                                                </div>
                                                <CardTitle className="text-lg">{pwd.name}</CardTitle>
                                            </div>
                                            <Button variant="ghost" size="icon" onClick={() => handleDeletePassword(pwd.id)}>
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        {pwd.username && (
                                            <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                                                <div>
                                                    <p className="text-xs text-muted-foreground">Benutzername</p>
                                                    <p className="text-sm font-mono">{pwd.username}</p>
                                                </div>
                                                <Button variant="ghost" size="icon" onClick={() => copyToClipboard(pwd.username, 'Benutzername')}>
                                                    <Copy className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        )}
                                        {pwd.email && (
                                            <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                                                <div>
                                                    <p className="text-xs text-muted-foreground">E-Mail</p>
                                                    <p className="text-sm font-mono">{pwd.email}</p>
                                                </div>
                                                <Button variant="ghost" size="icon" onClick={() => copyToClipboard(pwd.email, 'E-Mail')}>
                                                    <Copy className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                                            <div>
                                                <p className="text-xs text-muted-foreground">Passwort</p>
                                                <p className="text-sm font-mono">
                                                    {showPasswords[`pwd-${pwd.id}`] ? pwd.password : '••••••••'}
                                                </p>
                                            </div>
                                            <div className="flex gap-1">
                                                <Button variant="ghost" size="icon" onClick={() => togglePassword(`pwd-${pwd.id}`)}>
                                                    {showPasswords[`pwd-${pwd.id}`] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => copyToClipboard(pwd.password, 'Passwort')}>
                                                    <Copy className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                        {pwd.website_url && (
                                            <Button 
                                                variant="outline" 
                                                className="w-full" 
                                                onClick={() => window.open(pwd.website_url.startsWith('http') ? pwd.website_url : `https://${pwd.website_url}`, '_blank')}
                                            >
                                                <ExternalLink className="w-4 h-4 mr-2" />
                                                Website öffnen
                                            </Button>
                                        )}
                                        {pwd.notes && (
                                            <p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded">{pwd.notes}</p>
                                        )}
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                        {filteredPasswords.length === 0 && (
                            <div className="col-span-full text-center py-12 text-muted-foreground">
                                <KeyRound className="w-12 h-12 mx-auto mb-4 opacity-20" />
                                <p>Keine Passwörter gefunden</p>
                            </div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>

            {/* Tool Modal */}
            <Dialog open={showToolModal} onOpenChange={setShowToolModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Wrench className="w-5 h-5 text-primary" />
                            {editingTool ? 'Tool bearbeiten' : 'Neues Tool'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label>Name *</Label>
                            <Input
                                value={toolForm.name}
                                onChange={(e) => setToolForm({ ...toolForm, name: e.target.value })}
                                placeholder="z.B. Slack, Figma, Adobe CC"
                            />
                        </div>
                        <div>
                            <Label>Beschreibung</Label>
                            <Input
                                value={toolForm.description}
                                onChange={(e) => setToolForm({ ...toolForm, description: e.target.value })}
                                placeholder="Kurze Beschreibung"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Benutzername</Label>
                                <Input
                                    value={toolForm.username}
                                    onChange={(e) => setToolForm({ ...toolForm, username: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>E-Mail</Label>
                                <Input
                                    type="email"
                                    value={toolForm.email}
                                    onChange={(e) => setToolForm({ ...toolForm, email: e.target.value })}
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Passwort</Label>
                            <Input
                                type="password"
                                value={toolForm.password}
                                onChange={(e) => setToolForm({ ...toolForm, password: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Website URL</Label>
                            <Input
                                value={toolForm.website_url}
                                onChange={(e) => setToolForm({ ...toolForm, website_url: e.target.value })}
                                placeholder="https://..."
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2 pt-4 border-t">
                        <Button variant="outline" onClick={() => setShowToolModal(false)}>Abbrechen</Button>
                        <Button onClick={handleSaveTool} disabled={!toolForm.name}>
                            {editingTool ? 'Aktualisieren' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Password Modal */}
            <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-primary" />
                            Neues Passwort
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label>Name *</Label>
                            <Input
                                value={passwordForm.name}
                                onChange={(e) => setPasswordForm({ ...passwordForm, name: e.target.value })}
                                placeholder="z.B. Netflix, Amazon Prime"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Benutzername</Label>
                                <Input
                                    value={passwordForm.username}
                                    onChange={(e) => setPasswordForm({ ...passwordForm, username: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>E-Mail</Label>
                                <Input
                                    type="email"
                                    value={passwordForm.email}
                                    onChange={(e) => setPasswordForm({ ...passwordForm, email: e.target.value })}
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Passwort *</Label>
                            <Input
                                type="password"
                                value={passwordForm.password}
                                onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Website URL</Label>
                            <Input
                                value={passwordForm.website_url}
                                onChange={(e) => setPasswordForm({ ...passwordForm, website_url: e.target.value })}
                                placeholder="https://..."
                            />
                        </div>
                        <div>
                            <Label>Notizen</Label>
                            <Input
                                value={passwordForm.notes}
                                onChange={(e) => setPasswordForm({ ...passwordForm, notes: e.target.value })}
                                placeholder="Zusätzliche Informationen"
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2 pt-4 border-t">
                        <Button variant="outline" onClick={() => setShowPasswordModal(false)}>Abbrechen</Button>
                        <Button onClick={handleSavePassword} disabled={!passwordForm.name || !passwordForm.password}>
                            Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AccessPage;
